<template>
<div v-if='countOfPages > 1'
     class='fw-pagination'>
    <div class='fw-pagination__arrow fw-pagination__arrow--left'
         :class='{ "fw-pagination__arrow--invisible": current === pages[0] }'
         @click='prevPage'>
        <FwIcon
            class='fw-pagination__arrow__icon'
            icon='arrow-left'
            size='10'
            color='#213F6B'>
        </FwIcon>
    </div>
    <p v-for='page in pages'
       class='fw-current-page'
       :class='{
           "fw-current-page--active": isPage(page) && current === page,
           "fw-current-page--ellipsis": !isPage(page),
       }'
       @click='isPage(page) ? changePage(page) : null'>
        {{ page }}
    </p>
    <div class='fw-pagination__arrow fw-pagination__arrow--right'
         :class='{ "fw-pagination__arrow--invisible": current === pages[pages.length - 1] }'
         @click='nextPage'>
        <FwIcon
            class='fw-pagination__arrow__icon'
            icon='arrow-right'
            size='10'
            color='#213F6B'
            @click='nextPage'>
        </FwIcon>
    </div>
</div>
</template>

<script lang='ts'>
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

@Component
export default class Pagination extends Vue {
    @Prop({ type: Number, required: true }) totalPage!: number;
    @Prop({ type: Number, required: true }) currentPage!: number;
    @Prop({ type: Number, required: true }) perPage!: number;

    current: number = this.currentPage;
    countOfElements: number = 7;

    range(N: number, startFrom: number = 0): Array<number> {
        return Array.from({ length: N }, (v, k) => k + 1 + startFrom);
    }

    get countOfPages(): number {
        return Math.ceil(this.totalPage / this.perPage);
    }

    get pages(): Array<number | string> {
        const pages: Array<number | string> = [];
        if (this.countOfPages <= 1) return pages;
        if (this.countOfElements >= this.countOfPages) {
            for (let i = 1; i <= this.countOfPages; i++) {
                pages.push(i);
            }
        } else {
            if (this.current < this.countOfElements - 2) {
                return [...this.range(this.countOfElements - 2), '...', this.countOfPages];
            } else if (this.current >= this.countOfPages - 2) {
                return [1, '...', ...this.range(this.countOfElements - 2, this.countOfPages - 5)];
            }
            return [1, '...', ...this.range(this.countOfElements - 4, this.current - 2), '...', this.countOfPages];
        }
        return pages;
    }

    isPage(circleValue: number | string): boolean {
        return typeof circleValue === 'number' && Number.isInteger(circleValue);
    }

    changePage(page: number) {
        if (!this.isPage(page)) return;
        this.current = page;
        this.$emit(`getData`, page, this.perPage);
    }

    prevPage() {
        if (this.current !== 1) {
            this.current -= 1;
            this.$emit('getData', this.current, this.perPage);
        }
    }

    nextPage() {
        if (this.current !== this.pages[this.pages.length - 1]) {
            this.current += 1;
            this.$emit('getData', this.current, this.perPage);
        }
    }

    @Watch('currentPage')
    currentPageHandler(page: number): void {
        this.current = page;
    }
}
</script>

<style lang='sass'>
.fw-pagination
    position: fixed
    bottom: 0
    left: 0
    background: #F6F8F9
    z-index: 9
    width: 100%
    display: flex
    justify-content: center
    align-items: center
    user-select: none

    .fw-current-page
        padding: 0
        margin-left: 5px
        margin-right: 5px
        width: 25px
        height: 25px
        display: flex
        align-items: center
        justify-content: center
        font-weight: 500
        border-radius: 50%

        &:not(.fw-current-page--active):not(.fw-current-page--ellipsis)
            cursor: pointer
            &:hover
                background: #dffffb

        &--active
            background: $aqua-marine
            color: #fff
            cursor: default

    &__arrow
        display: flex
        align-items: center
        justify-content: center
        width: 25px
        height: 25px
        border-radius: 50%

        &__icon
            will-change: transform
            transition: transform .25s
            width: 100%
            height: 100%
            display: flex
            align-items: center
            justify-content: center

        &:not(.fw-pagination__arrow--invisible)
            cursor: pointer

            &:hover
                &.fw-pagination__arrow--left
                    background: #dffffb
                    .fw-pagination__arrow__icon
                        transform: translateX(-2px)

                &.fw-pagination__arrow--right
                    background: #dffffb
                    .fw-pagination__arrow__icon
                        transform: translateX(2px)

        &--invisible
            opacity: 0
            cursor: default
</style>
